<template>
    <section class="flex">
        <!-- TITLE -->
        <h3 class="m-3 flex-row baseline">
            {{ $l10n.__('Sellsy') }}
            <span class="flex-right">
                <CompassTooltip
                    :disabled="canEditSharing"
                    placement="bottom-end"
                    :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                >
                    <CompassButton
                        class="edit-button"
                        :label="$t.__('overview.tabs.SellsyInformationsSection.share')"
                        type="tertiary"
                        :disabled="!canEditSharing"
                        data-upsell="collaborative_sharing_suite"
                        :data-bot="databot['generalinfoedit2']"
                        @click="edit"
                    />
                </CompassTooltip>
            </span>
        </h3>

        <div class="px-3 bl-1">
            <div v-if="loading" class="prelative tab-loader">
                <preloader :display="loading" />
            </div>

            <div v-else>
                <h3 class="mt-0">{{ $l10n.__('Générales') }}</h3>
                <ul>
                    <li
                        v-if="infos.createdDate && infos.createdDate !== ''"
                        class="data-block data-block-hover"
                        data-bot="overview__general-info--date-created"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Date de création') }}
                        </span>
                        <span class="data-value">
                            <span>{{ infos.createdDate }}</span>
                        </span>
                    </li>
                    <li
                        v-if="infos.transformDate && infos.transformDate !== ''"
                        class="data-block data-block-hover"
                        data-bot="overview__general-info--date-transformation"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Date de transformation en client') }}
                        </span>
                        <span class="data-value">
                            <span>{{ infos.transformDate }}</span>
                        </span>
                    </li>
                    <li
                        v-if="infos.updatedDate && infos.updatedDate !== ''"
                        class="data-block data-block-hover"
                        data-bot="overview__general-info--date-last-update"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Date de dernière mise à jour') }}
                        </span>
                        <span class="data-value">
                            <span>{{ infos.updatedDate }}</span>
                        </span>
                    </li>
                    <li
                        v-if="infos.owner && Object.keys(infos.owner).length > 0"
                        class="data-block data-block-hover"
                        data-bot="overview__general-info--owner"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Propriétaire Sellsy') }}
                        </span>
                        <span class="data-value editable-user">
                            <avatar-inline v-if="!isThird" :data="infos.owner" size="small" />
                            <EditSingleUser
                                v-else
                                :data="infos.owner"
                                :canEdit="canEditSharing"
                                :loading="updatingOwner"
                                @update="handleOwnerUpdate"
                            />
                        </span>
                    </li>
                    <li
                        v-if="infos.sharedWithStaffs && infos.sharedWithStaffs.length > 0"
                        class="data-block data-block-hover"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Partagé avec') }}
                        </span>
                        <span
                            v-for="(shared, sharedIndex) in infos.sharedWithStaffs"
                            :key="sharedIndex"
                            class="data-value mb-2"
                        >
                            <avatar-inline :data="shared" size="small" />
                        </span>
                    </li>
                    <li
                        v-if="infos.sharedWithGroups && infos.sharedWithGroups.length > 0"
                        class="data-block data-block-hover"
                    >
                        <span class="data-label">
                            {{ $l10n.__('Groupe(s)') }}
                        </span>
                        <span
                            v-for="(group, groupIndex) in infos.sharedWithGroups"
                            :key="groupIndex"
                            class="data-value"
                        >
                            <span>{{ group.name }}</span>
                        </span>
                    </li>
                </ul>
            </div>

            <div v-if="canDisplayMassMailingConsent" class="data-block data-block-hover">
                <h3 class="mt-0">{{ $l10n.__('Marketing') }}</h3>
                <p class="data-label">
                    {{ $l10n.__('Inscription campagnes') }}
                </p>
                <el-checkbox-group
                    v-model="infos.campaigns"
                    :disabled="!permissions.can_edit_campaign_subscription"
                    class="flex-column"
                    @change="updateCampaign"
                >
                    <el-checkbox label="sms" data-bot="overview__general-info--marketing-sms">
                        {{ $l10n.__('SMS') }}
                    </el-checkbox>
                    <el-checkbox label="phone" data-bot="overview__general-info--marketing-phone">
                        {{ $l10n.__('Téléphonique') }}
                    </el-checkbox>
                    <el-checkbox label="email" data-bot="overview__general-info--marketing-email">
                        {{ $l10n.__('Emails') }}
                    </el-checkbox>
                    <el-checkbox label="postmail" data-bot="overview__general-info--marketing-mail">
                        {{ $l10n.__('Courrier') }}
                    </el-checkbox>
                    <el-checkbox label="custom" data-bot="overview__general-info--marketing-custom">
                        {{ $l10n.__('Marketing personnalisé') }}
                    </el-checkbox>
                </el-checkbox-group>
            </div>

            <tags
                v-if="enableTags && !isThird"
                :linkedid="linkedid"
                :linkedtype="linkedtype"
                :options="tags"
                :permissions="permissionsTag"
                class="tags-150 mt-5"
            />

            <div v-if="thirdType !== 'supplier' && infos.score && infos.score !== ''">
                <h4>{{ $l10n.__('Score') }}</h4>
                <div class="pointer" @click="openScoringHistory">
                    <span class="label mb-1 ellipsis">{{ infos.score }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import Tags from 'vcomponents/utilities/Tags.vue';
import AvatarInline from 'vcomponents/utilities/AvatarInline.vue';
import EditSingleUser from 'vcomponents/common/editInPlace/User/User.vue';
import CompanyApi from 'api/companies';
import ContactApi from 'api/contacts';
import { updateIndividual } from 'api/individuals';
import { mapState } from 'vuex';

export default {
    components: {
        Preloader,
        Tags,
        AvatarInline,
        EditSingleUser,
    },
    props: {
        linkedid: {
            type: Number,
            required: true,
        },
        linkedtype: {
            type: String,
            required: true,
        },
        enableTags: {
            type: Boolean,
        },
        canDisplayMassMailingConsent: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            updatingOwner: false,
        };
    },
    computed: {
        ...mapState('corp', {
            corpid: 'id',
        }),
        permissions() {
            return this.$store.getters.permissions;
        },
        tags() {
            return this.$store.getters.tags;
        },
        permissionsTag() {
            return {
                can_edit: this.permissions['can_edit'] ? this.permissions['can_edit'] : false,
                can_create: this.permissions['can_create_new_tag_value']
                    ? this.permissions['can_create_new_tag_value']
                    : false,
            };
        },
        thirdType() {
            return this.$store.getters.identity.type;
        },
        isThird() {
            return ['client', 'prospect', 'supplier', 'contact'].includes(this.thirdType);
        },
        isIndividual() {
            if (
                ['client', 'prospect', 'supplier'].includes(this.thirdType) &&
                !this.$store.getters.identity.isCorporation
            ) {
                return true;
            }
            return false;
        },
        infos() {
            return this.$store.getters.sellsyInformations;
        },
        databot() {
            return this.$store.getters.databots;
        },
        canEditSharing() {
            return this.permissions ? this.permissions['can_edit_sharing'] : true;
        },
    },
    methods: {
        edit() {
            window.Sharepoint.editForm(
                '' + this.$store.getters.linkedtype + '',
                this.$store.getters.linkedid,
            );
        },
        openScoringHistory() {
            window.ScoringEvent.history(this.corpid, this.linkedid);
        },
        updateCampaign: _.debounce(function () {
            SellsyJS.WebServiceCaller.put(
                `/rest/${this.linkedtype}s/${this.linkedid}/campaigns/subscription`,
                {},
                this.infos.campaigns,
                {
                    headers: true,
                    callback: (response) => {
                        if (response.status === 202) {
                            Push.success(
                                this.$l10n.__(
                                    "Vous avez modifié les préférences d'inscription aux campagnes marketing de la société, un email lui a été envoyé pour confirmer la mise à jour de ses inscriptions.",
                                ),
                                false,
                            );
                        }
                    },
                },
                () => {
                    Push.error(
                        this.$l10n.__(
                            'Une erreur est survenue, veuillez ré-essayer ultérieurement.',
                        ),
                        false,
                    );
                },
            );
        }, 1000),
        adaptCivility(civility) {
            return civility.toLowerCase() === 'mr'
                ? this.$t.__('common.labels.civility.mr')
                : this.$t.__('common.labels.civility.mrs');
        },
        updateOwner(newOwner) {
            this.$store.dispatch('updateOwner', {
                avatar: {
                    type: newOwner.avatar ? 'picture' : 'initials',
                    value: newOwner.avatar ?? `${newOwner.firstname[0]}${newOwner.lastname[0]}`,
                },
                value: `${newOwner.firstname[0]}${newOwner.lastname[0]}`,
                civility: newOwner.civility ? this.adaptCivility(newOwner.civility) : 'Mr',
                email: newOwner.email,
                firstname: newOwner.firstname,
                id: newOwner.id,
                lastname: newOwner.lastname,
                link: `/staff/${newOwner.id}`,
                mobile: newOwner.mobile_number ?? '',
                name: `${newOwner.firstname} ${newOwner.lastname}`,
            });
        },
        handleOwnerUpdateError() {
            this.$pushToast({
                type: 'error',
                close: true,
                text: this.$t.__('overview.share.updateError'),
            });
        },
        async updateOwnerWithApi(apiMethod, linkedid, embed, owner) {
            try {
                const response = await apiMethod(linkedid, embed, owner);
                const newOwner = response.data._embed.owner;
                this.updateOwner(newOwner);
                this.$pushToast({
                    type: 'success',
                    close: true,
                    text: this.$t.__('overview.third.update.title.success'),
                });
            } catch (error) {
                const errorList = [];
                if (error.response?.data?.error?.details) {
                    errorList.push(
                        ...Object.entries(error.response?.data?.error?.details).map(
                            ([key, value]) => `${key}: ${value.toLowerCase()}`,
                        ),
                    );
                }

                if (errorList.length > 0) {
                    this.$pushToast({
                        type: 'error',
                        errorList,
                        close: true,
                        text: this.$t.__('overview.share.updateValidationError'),
                    });
                } else {
                    this.handleOwnerUpdateError();
                }
            } finally {
                this.updatingOwner = false;
            }
        },
        handleOwnerUpdate(value) {
            this.updatingOwner = true;

            const apiMap = {
                individual: updateIndividual,
                client: CompanyApi.updateCompany,
                prospect: CompanyApi.updateCompany,
                supplier: CompanyApi.updateCompany,
                contact: ContactApi.updateContact,
            };

            const apiMethod = this.isIndividual ? apiMap.individual : apiMap[this.thirdType];

            if (apiMethod) {
                this.updateOwnerWithApi(
                    apiMethod,
                    this.linkedid,
                    { 'embed[]': 'owner' },
                    { owner_id: value },
                );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.tab-loader {
    height: 200px;
}

.data-value {
    &.editable-user {
        margin: var(--spacing-0) 0 0;
    }
}

:deep() {
    .upsell-action {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);

        &:hover {
            background: none;
        }

        .compass-button__content {
            padding-right: 0 !important;
            .compass-button__label {
                color: var(--colors-common-text-standard-disabled);
            }
        }

        &:hover {
            color: var(--colors-common-background-disabled);
        }
    }
}
</style>
